/**
 * Реализация менеджера реактивности и computed свойств
 */

import { EventEmitter } from 'eventemitter3';
import {
  ReactivityManager,
  ComputedProperty,
  ComputedFunction,
  ComputedConfig,
  ReactiveWatcher,
  WatchCallback,
  WatcherConfig,
  ReactiveProperty,
  ReactiveRef,
  DependencyGraph,
  DependencyNode,
  ReactivityStats,
  ReactivityConfig,
  ReactivityEvent,
  ReactivityEventData,
  ReactivityEventCallback,
  ReactiveContext,
  ReactiveScope
} from '../types/reactivity-interfaces';

export class ReactivityManagerImpl extends EventEmitter implements ReactivityManager {
  private computedProperties = new Map<string, ComputedProperty>();
  private watchers = new Map<string, ReactiveWatcher>();
  private reactiveProperties = new Map<string, ReactiveProperty>();
  private refs = new Map<string, ReactiveRef>();
  private scopes = new Map<string, ReactiveScope>();
  private config: ReactivityConfig;
  private currentlyComputing: Set<string> = new Set();
  private notificationQueue: Array<{ path: string[], newValue: any, oldValue: any }> = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private statsInterval: NodeJS.Timeout | null = null;
  private memoryLeakCheckInterval: NodeJS.Timeout | null = null;
  
  // Контекст выполнения для отслеживания иерархии
  private context: ReactiveContext = {
    computedStack: [],
    watcherStack: [],
    cleanupFns: []
  };
  
  private stats = {
    computedCount: 0,
    watcherCount: 0,
    refCount: 0,
    scopeCount: 0,
    disposedCount: 0,
    notificationsPerSecond: 0,
    averageComputeTime: 0,
    memoryUsage: 0,
    totalComputeTime: 0,
    computeCallCount: 0
  };
  private notificationHistory: number[] = [];

  constructor(config: ReactivityConfig = {}) {
    super();
    this.config = {
      maxComputed: 1000,
      maxWatchers: 10000,
      defaultTTL: 60000, // 1 минута
      maxDepth: 10,
      batchNotifications: true,
      batchSize: 100,
      debug: false,
      autoDispose: true,
      maxDisposeDepth: 50,
      warnMemoryLeaks: true,
      ...config
    };

    // Обновляем статистику каждую секунду
    this.statsInterval = setInterval(() => this.updateStats(), 1000);
    
    // Проверяем утечки памяти каждые 30 секунд
    if (this.config.warnMemoryLeaks) {
      this.memoryLeakCheckInterval = setInterval(() => this.checkMemoryLeaks(), 30000);
    }
  }

  computed<T>(compute: ComputedFunction<T>, config: ComputedConfig = {}): ComputedProperty<T> {
    if (this.computedProperties.size >= this.config.maxComputed!) {
      throw new Error(`Превышен лимит computed свойств: ${this.config.maxComputed}`);
    }

    const id = this.generateId('computed');
    const computedConfig = {
      lazy: true,
      cache: true,
      ttl: this.config.defaultTTL,
      maxDepth: this.config.maxDepth,
      debug: this.config.debug,
      autoDispose: this.config.autoDispose,
      ...config
    };

    // Определяем родителя из текущего контекста
    const parent = config.parent || this.context.currentComputed;

    const computedProperty: ComputedProperty<T> = {
      id,
      compute,
      dependencies: new Set(),
      cachedValue: undefined as any,
      isValid: false,
      lastUpdated: 0,
      config: computedConfig,
      parent,
      children: new Set(),
      isDisposed: false
    };

    // Устанавливаем функцию очистки
    computedProperty.dispose = () => {
      this.disposeComputed(id);
    };

    this.computedProperties.set(id, computedProperty);
    this.stats.computedCount++;

    // Устанавливаем связь родитель-ребенок
    if (parent) {
      this.setParent(id, parent, 'computed');
    }

    // Если не ленивое, сразу вычисляем
    if (!computedConfig.lazy) {
      this.getComputedValue(id);
    }

    this.emitEvent('computed-created', {
      computedId: id,
      dependencies: Array.from(computedProperty.dependencies),
      parent
    });

    return computedProperty;
  }

  watch(path: string | string[], callback: WatchCallback, config: WatcherConfig = {}): ReactiveWatcher {
    if (this.watchers.size >= this.config.maxWatchers!) {
      throw new Error(`Превышен лимит наблюдателей: ${this.config.maxWatchers}`);
    }

    const id = this.generateId('watcher');
    const pathArray = Array.isArray(path) ? path : [path];
    const pathKey = pathArray.join('.');

    const watcherConfig = {
      immediate: false,
      deep: false,
      flush: 'async' as const,
      debug: this.config.debug,
      autoDispose: this.config.autoDispose,
      ...config
    };

    // Определяем родителя из текущего контекста
    const parent = config.parent || this.context.currentComputed || this.context.currentWatcher;

    const watcher: ReactiveWatcher = {
      id,
      path: pathArray,
      callback,
      config: watcherConfig,
      active: true,
      parent,
      children: new Set(),
      isDisposed: false
    };

    // Устанавливаем функцию очистки
    watcher.dispose = () => {
      this.disposeWatcher(id);
    };

    this.watchers.set(id, watcher);
    this.stats.watcherCount++;

    // Регистрируем наблюдатель для свойства
    if (!this.reactiveProperties.has(pathKey)) {
      this.reactiveProperties.set(pathKey, {
        path: pathArray,
        watchers: new Set(),
        computedDependents: new Set()
      });
    }

    this.reactiveProperties.get(pathKey)!.watchers.add(watcher);

    // Устанавливаем связь родитель-ребенок
    if (parent) {
      this.setParent(id, parent, 'watcher');
    }

    this.emitEvent('watcher-created', {
      watcherId: id,
      path: pathArray,
      parent
    });

    // Немедленный вызов если настроен
    if (watcherConfig.immediate) {
      try {
        // Устанавливаем контекст для отслеживания дочерних элементов
        const previousWatcher = this.context.currentWatcher;
        this.context.currentWatcher = id;
        this.context.watcherStack.push(id);

        callback(undefined, undefined, pathArray);

        this.context.watcherStack.pop();
        this.context.currentWatcher = previousWatcher;
      } catch (error) {
        console.error(`Ошибка в немедленном вызове наблюдателя ${id}:`, error);
      }
    }

    return watcher;
  }

  unwatch(watcherId: string): boolean {
    const watcher = this.watchers.get(watcherId);
    if (!watcher || watcher.isDisposed) {
      return false;
    }
    this.disposeWatcher(watcherId);
    return true;
  }

  getComputedValue<T>(computedId: string): T | undefined {
    const computed = this.computedProperties.get(computedId);
    if (!computed || computed.isDisposed) {
      return undefined;
    }

    // Проверяем валидность кэша
    if (computed.isValid && computed.config.cache) {
      if (computed.config.ttl && Date.now() - computed.lastUpdated > computed.config.ttl) {
        computed.isValid = false;
      } else {
        return computed.cachedValue;
      }
    }

    // Проверяем циклические зависимости
    if (this.currentlyComputing.has(computedId)) {
      throw new Error(`Обнаружена циклическая зависимость в computed ${computedId}`);
    }

    this.currentlyComputing.add(computedId);
    
    // Устанавливаем контекст для отслеживания дочерних элементов
    const previousComputed = this.context.currentComputed;
    this.context.currentComputed = computedId;
    this.context.computedStack.push(computedId);

    try {
      const startTime = performance.now();

      // Очищаем старые зависимости только если не кэшируется
      if (!computed.config.cache || !computed.isValid) {
        this.clearComputedDependencies(computedId);
      }

      // Если computed пересоздается и включен auto-dispose, очищаем детей
      if (computed.config.autoDispose && computed.children.size > 0) {
        this.disposeChildren(computedId);
      }

      // Устанавливаем отслеживание зависимостей
      this.currentDependencyTracker = computedId;

      // Вычисляем новое значение
      const value = computed.compute();
      
      computed.cachedValue = value;
      computed.isValid = true;
      computed.lastUpdated = Date.now();

      const endTime = performance.now();
      const computeTime = endTime - startTime;
      
      this.stats.totalComputeTime += computeTime;
      this.stats.computeCallCount++;

      this.emitEvent('computed-updated', {
        computedId,
        dependencies: Array.from(computed.dependencies)
      });

      return value;
    } catch (error) {
      computed.isValid = false;
      throw error;
    } finally {
      this.currentDependencyTracker = null;
      this.currentlyComputing.delete(computedId);
      this.context.computedStack.pop();
      this.context.currentComputed = previousComputed;
    }
  }

  // === AUTO-DISPOSE МЕТОДЫ ===

  createScope(parentId?: string): ReactiveScope {
    const id = this.generateId('scope');
    
    const scope: ReactiveScope = {
      id,
      computedProperties: new Set(),
      watchers: new Set(),
      parent: parentId,
      children: new Set(),
      isDisposed: false,
      dispose: () => this.disposeScope(id)
    };

    this.scopes.set(id, scope);
    this.stats.scopeCount++;

    // Устанавливаем связь с родительской областью
    if (parentId) {
      const parentScope = this.scopes.get(parentId);
      if (parentScope && !parentScope.isDisposed) {
        parentScope.children.add(id);
      }
    }

    this.emitEvent('scope-created', {
      scopeId: id,
      parent: parentId
    });

    return scope;
  }

  runInScope<T>(scopeId: string, fn: () => T): T {
    const scope = this.scopes.get(scopeId);
    if (!scope || scope.isDisposed) {
      throw new Error(`Область видимости ${scopeId} не найдена или была disposed`);
    }

    // Сохраняем текущий контекст
    const previousContext = { ...this.context };
    
    try {
      // Выполняем функцию в контексте области
      return fn();
    } finally {
      // Восстанавливаем контекст
      this.context = previousContext;
    }
  }

  disposeScope(scopeId: string): void {
    const scope = this.scopes.get(scopeId);
    if (!scope || scope.isDisposed) {
      return;
    }

    // Сначала очищаем все дочерние области
    for (const childId of scope.children) {
      this.disposeScope(childId);
    }

    // Очищаем все computed в этой области
    for (const computedId of scope.computedProperties) {
      this.disposeComputed(computedId);
    }

    // Очищаем все watchers в этой области
    for (const watcherId of scope.watchers) {
      this.disposeWatcher(watcherId);
    }

    // Удаляем связь с родительской областью
    if (scope.parent) {
      const parentScope = this.scopes.get(scope.parent);
      if (parentScope) {
        parentScope.children.delete(scopeId);
      }
    }

    scope.isDisposed = true;
    this.scopes.delete(scopeId);
    this.stats.scopeCount--;
    this.stats.disposedCount++;

    this.emitEvent('scope-disposed', {
      scopeId,
      parent: scope.parent
    });
  }

  disposeComputed(computedId: string): void {
    const computed = this.computedProperties.get(computedId);
    if (!computed || computed.isDisposed) {
      return;
    }

    // Сначала очищаем всех детей
    this.disposeChildren(computedId);

    // Удаляем зависимости
    this.clearComputedDependencies(computedId);

    // Удаляем связь с родителем
    if (computed.parent) {
      this.removeFromParent(computedId, computed.parent);
    }

    computed.isDisposed = true;
    this.computedProperties.delete(computedId);
    this.stats.computedCount--;
    this.stats.disposedCount++;

    this.emitEvent('computed-disposed', {
      computedId,
      parent: computed.parent,
      children: Array.from(computed.children)
    });
  }

  disposeWatcher(watcherId: string): void {
    const watcher = this.watchers.get(watcherId);
    if (!watcher || watcher.isDisposed) {
      return;
    }

    // Сначала очищаем всех детей
    this.disposeChildren(watcherId);

    // Удаляем из реактивных свойств
    const pathKey = watcher.path.join('.');
    const property = this.reactiveProperties.get(pathKey);
    if (property) {
      property.watchers.delete(watcher);
      if (property.watchers.size === 0 && property.computedDependents.size === 0) {
        this.reactiveProperties.delete(pathKey);
      }
    }

    // Удаляем связь с родителем
    if (watcher.parent) {
      this.removeFromParent(watcherId, watcher.parent);
    }

    watcher.isDisposed = true;
    this.watchers.delete(watcherId);
    this.stats.watcherCount--;
    this.stats.disposedCount++;

    this.emitEvent('watcher-disposed', {
      watcherId,
      parent: watcher.parent,
      children: Array.from(watcher.children)
    });
  }

  getCurrentContext(): ReactiveContext {
    return { ...this.context };
  }

  setParent(childId: string, parentId: string, type: 'computed' | 'watcher'): void {
    if (type === 'computed') {
      const child = this.computedProperties.get(childId);
      const parent = this.computedProperties.get(parentId);
      
      if (child && parent && !child.isDisposed && !parent.isDisposed) {
        child.parent = parentId;
        parent.children.add(childId);
      }
    } else {
      const child = this.watchers.get(childId);
      
      // Родитель может быть как computed, так и watcher
      const parentComputed = this.computedProperties.get(parentId);
      const parentWatcher = this.watchers.get(parentId);
      
      if (child && !child.isDisposed) {
        if (parentComputed && !parentComputed.isDisposed) {
          child.parent = parentId;
          parentComputed.children.add(childId);
        } else if (parentWatcher && !parentWatcher.isDisposed) {
          child.parent = parentId;
          parentWatcher.children.add(childId);
        }
      }
    }
  }

  getChildren(parentId: string, type?: 'computed' | 'watcher'): string[] {
    const computed = this.computedProperties.get(parentId);
    const watcher = this.watchers.get(parentId);
    
    let children: Set<string> = new Set();
    
    if (computed && !computed.isDisposed) {
      children = new Set([...children, ...computed.children]);
    }
    
    if (watcher && !watcher.isDisposed) {
      children = new Set([...children, ...watcher.children]);
    }

    if (type) {
      return Array.from(children).filter(childId => {
        if (type === 'computed') {
          return this.computedProperties.has(childId);
        } else {
          return this.watchers.has(childId);
        }
      });
    }

    return Array.from(children);
  }

  isDisposed(id: string, type: 'computed' | 'watcher'): boolean {
    if (type === 'computed') {
      const computed = this.computedProperties.get(id);
      return !computed || computed.isDisposed;
    } else {
      const watcher = this.watchers.get(id);
      return !watcher || watcher.isDisposed;
    }
  }

  // === ПРИВАТНЫЕ МЕТОДЫ ===

  private disposeChildren(parentId: string): void {
    const computed = this.computedProperties.get(parentId);
    const watcher = this.watchers.get(parentId);
    
    const children = new Set<string>();
    
    if (computed) {
      for (const childId of computed.children) {
        children.add(childId);
      }
      computed.children.clear();
    }
    
    if (watcher) {
      for (const childId of watcher.children) {
        children.add(childId);
      }
      watcher.children.clear();
    }

    // Рекурсивно очищаем всех детей
    for (const childId of children) {
      if (this.computedProperties.has(childId)) {
        this.disposeComputed(childId);
      } else if (this.watchers.has(childId)) {
        this.disposeWatcher(childId);
      }
    }
  }

  private removeFromParent(childId: string, parentId: string): void {
    const parentComputed = this.computedProperties.get(parentId);
    const parentWatcher = this.watchers.get(parentId);
    
    if (parentComputed) {
      parentComputed.children.delete(childId);
    }
    
    if (parentWatcher) {
      parentWatcher.children.delete(childId);
    }
  }

  private checkMemoryLeaks(): void {
    const now = Date.now();
    const leakThreshold = 300000; // 5 минут
    
    // Проверяем computed свойства
    for (const [id, computed] of this.computedProperties) {
      if (!computed.isDisposed && (now - computed.lastUpdated) > leakThreshold) {
        if (computed.dependencies.size === 0 && computed.children.size === 0) {
          this.emitEvent('memory-leak-detected', {
            leakInfo: {
              type: 'computed',
              id,
              age: now - computed.lastUpdated,
              dependencies: computed.dependencies.size
            }
          });
        }
      }
    }
    
    // Проверяем watchers
    for (const [id, watcher] of this.watchers) {
      if (!watcher.isDisposed && watcher.children.size === 0) {
        // Если watcher давно не использовался и у него нет детей
        this.emitEvent('memory-leak-detected', {
          leakInfo: {
            type: 'watcher',
            id,
            age: now,
            dependencies: 0
          }
        });
      }
    }
  }

  invalidateComputed(computedId: string): void {
    const computed = this.computedProperties.get(computedId);
    if (computed && !computed.isDisposed) {
      computed.isValid = false;
      this.emitEvent('computed-invalidated', {
        computedId
      });
    }
  }

  invalidateByPath(path: string[]): void {
    const pathKey = path.join('.');
    const property = this.reactiveProperties.get(pathKey);
    if (property) {
      for (const computedId of property.computedDependents) {
        this.invalidateComputed(computedId);
      }
    }
  }

  notify(path: string[], newValue: any, oldValue: any): void {
    if (this.config.batchNotifications) {
      this.notificationQueue.push({ path, newValue, oldValue });
      this.scheduleBatchNotification();
    } else {
      this.processNotification(path, newValue, oldValue);
    }
  }

  private scheduleBatchNotification(): void {
    if (this.batchTimeout) return;
    
    this.batchTimeout = setTimeout(() => {
      this.processBatchNotifications();
      this.batchTimeout = null;
    }, 0);
  }

  private processBatchNotifications(): void {
    const notifications = this.notificationQueue.splice(0, this.config.batchSize!);
    
    for (const notification of notifications) {
      this.processNotification(notification.path, notification.newValue, notification.oldValue);
    }
    
    if (this.notificationQueue.length > 0) {
      this.scheduleBatchNotification();
    }
  }

  private processNotification(path: string[], newValue: any, oldValue: any): void {
    const pathKey = path.join('.');
    const property = this.reactiveProperties.get(pathKey);
    
    if (!property) return;

    this.notificationHistory.push(Date.now());

    // Уведомляем watchers
    for (const watcher of property.watchers) {
      if (watcher.active && !watcher.isDisposed) {
        try {
          // Устанавливаем контекст для отслеживания дочерних элементов
          const previousWatcher = this.context.currentWatcher;
          this.context.currentWatcher = watcher.id;
          this.context.watcherStack.push(watcher.id);

          if (watcher.config.flush === 'sync') {
            watcher.callback(newValue, oldValue, path);
          } else {
            setTimeout(() => {
              if (!watcher.isDisposed) {
                watcher.callback(newValue, oldValue, path);
              }
            }, 0);
          }

          this.context.watcherStack.pop();
          this.context.currentWatcher = previousWatcher;

          this.emitEvent('watcher-triggered', {
            watcherId: watcher.id,
            path,
            newValue,
            oldValue
          });
        } catch (error) {
          console.error(`Ошибка в наблюдателе ${watcher.id}:`, error);
        }
      }
    }

    // Инвалидируем computed свойства
    for (const computedId of property.computedDependents) {
      this.invalidateComputed(computedId);
    }

    this.emitEvent('property-changed', {
      path,
      newValue,
      oldValue
    });
  }

  ref<T>(value: T): ReactiveRef<T> {
    const id = this.generateId('ref');
    const manager = this;
    
    const ref: ReactiveRef<T> = {
      id,
      watchers: new Set(),
      isDisposed: false,
      dispose: () => {
        ref.isDisposed = true;
        this.refs.delete(id);
        this.stats.refCount--;
        this.stats.disposedCount++;
      },
      get value() {
        // Отслеживаем зависимость
        if (manager.currentDependencyTracker) {
          manager.trackDependency(manager.currentDependencyTracker, [id]);
        }
        return value;
      },
      set value(newValue: T) {
        const oldValue = value;
        value = newValue;
        manager.notify([id], newValue, oldValue);
      }
    };

    this.refs.set(id, ref);
    this.stats.refCount++;

    return ref;
  }

  reactive<T extends object>(obj: T): T {
    return this.createReactiveProxy(obj, []);
  }

  private createReactiveProxy<T extends object>(obj: T, basePath: string[]): T {
    return new Proxy(obj, {
      get: (target, prop: string | symbol) => {
        if (typeof prop === 'symbol') return target[prop as keyof T];
        
        const path = [...basePath, prop];
        
        // Отслеживаем зависимость
        if (this.currentDependencyTracker) {
          this.trackDependency(this.currentDependencyTracker, path);
        }
        
        const value = target[prop as keyof T];
        
        // Если значение - объект, создаем для него реактивный прокси
        if (typeof value === 'object' && value !== null) {
          return this.createReactiveProxy(value, path);
        }
        
        return value;
      },
      
      set: (target, prop: string | symbol, value) => {
        if (typeof prop === 'symbol') {
          target[prop as keyof T] = value;
          return true;
        }
        
        const path = [...basePath, prop];
        const oldValue = target[prop as keyof T];
        
        target[prop as keyof T] = value;
        this.notify(path, value, oldValue);
        
        return true;
      }
    });
  }

  private currentDependencyTracker: string | null = null;

  private trackDependency(computedId: string, path: string[]): void {
    const computed = this.computedProperties.get(computedId);
    if (!computed || computed.isDisposed) return;

    const pathKey = path.join('.');
    computed.dependencies.add(pathKey);

    // Регистрируем computed как зависимый от этого свойства
    if (!this.reactiveProperties.has(pathKey)) {
      this.reactiveProperties.set(pathKey, {
        path,
        watchers: new Set(),
        computedDependents: new Set()
      });
    }

    this.reactiveProperties.get(pathKey)!.computedDependents.add(computedId);

    this.emitEvent('dependency-added', {
      computedId,
      path
    });
  }

  private clearComputedDependencies(computedId: string): void {
    const computed = this.computedProperties.get(computedId);
    if (!computed) return;

    for (const pathKey of computed.dependencies) {
      const property = this.reactiveProperties.get(pathKey);
      if (property) {
        property.computedDependents.delete(computedId);
        
        if (property.watchers.size === 0 && property.computedDependents.size === 0) {
          this.reactiveProperties.delete(pathKey);
        }
        
        this.emitEvent('dependency-removed', {
          computedId,
          path: property.path
        });
      }
    }

    computed.dependencies.clear();
  }

  getDependencies(computedId: string): string[] {
    const computed = this.computedProperties.get(computedId);
    return computed ? Array.from(computed.dependencies) : [];
  }

  getDependencyGraph(): DependencyGraph {
    const nodes = new Map<string, DependencyNode>();
    const edges = new Map<string, Set<string>>();

    // Добавляем узлы для всех свойств
    for (const [pathKey, property] of this.reactiveProperties) {
      nodes.set(pathKey, {
        path: property.path,
        type: 'property',
        dependentCount: property.computedDependents.size + property.watchers.size,
        dependencyCount: 0
      });
    }

    // Добавляем узлы для computed свойств
    for (const [computedId, computed] of this.computedProperties) {
      if (!computed.isDisposed) {
        nodes.set(computedId, {
          path: [computedId],
          type: 'computed',
          dependentCount: computed.children.size,
          dependencyCount: computed.dependencies.size
        });

        // Добавляем рёбра зависимостей
        edges.set(computedId, new Set(computed.dependencies));
      }
    }

    // Добавляем узлы для refs
    for (const [refId, ref] of this.refs) {
      if (!ref.isDisposed) {
        nodes.set(refId, {
          path: [refId],
          type: 'ref',
          dependentCount: ref.watchers.size,
          dependencyCount: 0
        });
      }
    }

    return { nodes, edges };
  }

  clear(): void {
    // Очищаем все области видимости
    for (const scopeId of this.scopes.keys()) {
      this.disposeScope(scopeId);
    }

    this.computedProperties.clear();
    this.watchers.clear();
    this.reactiveProperties.clear();
    this.refs.clear();
    this.scopes.clear();
    this.currentlyComputing.clear();
    this.notificationQueue = [];
    
    // Сбрасываем контекст
    this.context = {
      computedStack: [],
      watcherStack: [],
      cleanupFns: []
    };
    
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    if (this.statsInterval) {
      clearInterval(this.statsInterval);
      this.statsInterval = null;
    }
    if (this.memoryLeakCheckInterval) {
      clearInterval(this.memoryLeakCheckInterval);
      this.memoryLeakCheckInterval = null;
    }
    
    this.stats = {
      computedCount: 0,
      watcherCount: 0,
      refCount: 0,
      scopeCount: 0,
      disposedCount: 0,
      notificationsPerSecond: 0,
      averageComputeTime: 0,
      memoryUsage: 0,
      totalComputeTime: 0,
      computeCallCount: 0
    };
  }

  getStats(): ReactivityStats {
    return {
      computedCount: this.stats.computedCount,
      watcherCount: this.stats.watcherCount,
      refCount: this.stats.refCount,
      scopeCount: this.stats.scopeCount,
      disposedCount: this.stats.disposedCount,
      notificationsPerSecond: this.stats.notificationsPerSecond,
      averageComputeTime: this.stats.averageComputeTime,
      memoryUsage: this.estimateMemoryUsage()
    };
  }

  private updateStats(): void {
    // Подсчитываем уведомления за последнюю секунду
    const now = Date.now();
    this.notificationHistory = this.notificationHistory.filter(time => now - time < 1000);
    this.stats.notificationsPerSecond = this.notificationHistory.length;
    
    // Обновляем среднее время вычисления
    if (this.stats.computeCallCount > 0) {
      this.stats.averageComputeTime = this.stats.totalComputeTime / this.stats.computeCallCount;
    }
  }

  private estimateMemoryUsage(): number {
    let memoryUsage = 0;
    
    // Примерная оценка использования памяти
    memoryUsage += this.computedProperties.size * 500; // ~500 байт на computed
    memoryUsage += this.watchers.size * 300; // ~300 байт на watcher
    memoryUsage += this.reactiveProperties.size * 200; // ~200 байт на свойство
    memoryUsage += this.refs.size * 100; // ~100 байт на ref
    memoryUsage += this.scopes.size * 400; // ~400 байт на scope
    
    return memoryUsage;
  }

  private generateId(prefix: string): string {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private emitEvent(event: ReactivityEvent, data: Partial<ReactivityEventData>): void {
    this.emit(event, {
      event,
      timestamp: Date.now(),
      ...data
    });
  }

  onReactivityEvent(event: ReactivityEvent, callback: ReactivityEventCallback): void {
    this.on(event, callback);
  }

  offReactivityEvent(event: ReactivityEvent, callback: ReactivityEventCallback): void {
    this.off(event, callback);
  }
} 